import { Injectable, inject } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '@root/environments/environment';

@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | undefined;
  private readonly router = inject(Router);

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    // Browser telemetry only ships from the production build. Azure Monitor's ingestion hostname is
    // regional and shared, and the private DNS zone in the Xemex network resolves it to a private
    // address — so from a Xemex machine the browser refuses to post from a public page to a private
    // one, and every page view logs a CORS error the SDK cannot suppress. The dev and white-label
    // builds are only ever opened from that network, so their telemetry never arrived anyway.
    if (!environment.production) {
      return;
    }

    const connectionString = (
      environment as { monitoring?: { appInsightsConnectionString?: string } }
    ).monitoring?.appInsightsConnectionString;
    if (!connectionString) {
      console.warn('Application Insights connection string not configured');
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        enableAutoRouteTracking: true,
        enableRequestHeaderTracking: true,
        enableCorsCorrelation: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        maxAjaxCallsPerView: 500,
        disableFetchTracking: false,
        disableCorrelationHeaders: false,
        correlationHeaderDomains: ['*.xemex-cloud.eu', '*.azurewebsites.net'],
      },
    });

    this.appInsights.loadAppInsights();
    this.appInsights.trackPageView();

    this.trackRouteChanges();
  }

  private trackRouteChanges(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackPageView(event.urlAfterRedirects);
      });
  }

  trackPageView(name?: string, url?: string): void {
    this.appInsights?.trackPageView({ name, uri: url });
  }

  trackEvent(name: string, properties?: Record<string, string | number | boolean>): void {
    this.appInsights?.trackEvent({ name }, properties);
  }

  trackException(error: Error, properties?: Record<string, string | number | boolean>): void {
    this.appInsights?.trackException({
      exception: error,
      properties,
    });
  }

  trackMetric(
    name: string,
    value: number,
    properties?: Record<string, string | number | boolean>,
  ): void {
    this.appInsights?.trackMetric({ name, average: value }, properties);
  }

  trackTrace(
    message: string,
    severityLevel?: number,
    properties?: Record<string, string | number | boolean>,
  ): void {
    this.appInsights?.trackTrace({ message, severityLevel }, properties);
  }

  setAuthenticatedUserId(userId: string, accountId?: string): void {
    this.appInsights?.setAuthenticatedUserContext(userId, accountId ?? undefined);
  }

  clearAuthenticatedUserId(): void {
    this.appInsights?.clearAuthenticatedUserContext();
  }

  startTrackEvent(name: string): void {
    this.appInsights?.startTrackEvent(name);
  }

  stopTrackEvent(name: string, properties?: Record<string, string>): void {
    this.appInsights?.stopTrackEvent(name, properties);
  }

  flush(): void {
    this.appInsights?.flush();
  }
}
